import { ApolloClient, ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { categoryFragment } from '../../client/fragments/category';
import {
    CategoryFieldsFragment,
    GetCategoriesQuery,
    GetSingleCategoryQuery,
    GetSingleCategoryQueryVariables,
} from '../../client/generated/graphql';
import { getCategoriesQuery } from '../../client/queries/category';
import { getSingleCategoryQuery } from '../../client/queries/singleCategory';
import { ListCategory } from '../../client/types';

export const useCachedCategory = (categoryId?: string | null) => {
    const client = useApolloClient();

    if (!categoryId) return null;

    try {
        return client.cache.readFragment<CategoryFieldsFragment>({
            id: client.cache.identify({ __typename: 'Category', id: categoryId }),
            fragment: categoryFragment,
            fragmentName: 'CategoryFields',
        });
    } catch {
        return null;
    }
};

export const readCachedCategories = (client: ApolloClient<object>): ListCategory[] => {
    // Get all Category objects from cache regardless of which query fetched them
    const cacheData = client.cache.extract();
    return Object.entries(cacheData)
        .filter(([key, value]: [string, any]) => key.startsWith('Category:') || value?.__typename === 'Category')
        .map(([_key, value]) => value as ListCategory);
};

export const useCachedCategories = (): ListCategory[] => {
    const client = useApolloClient();

    return useMemo(() => readCachedCategories(client), [client.cache]);
};

export const useCachedCategoryWithFallback = (
    categoryId?: string | null,
): {
    category: CategoryFieldsFragment | null;
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const client = useApolloClient();

    // Try to read from normalized cache first
    const cachedCategory = categoryId
        ? client.cache.readFragment<CategoryFieldsFragment>({
              id: client.cache.identify({ __typename: 'Category', id: categoryId }),
              fragment: categoryFragment,
              fragmentName: 'CategoryFields',
          })
        : null;

    // If not in cache, fetch from network
    const {
        data: networkData,
        loading,
        error,
    } = useQuery<GetSingleCategoryQuery, GetSingleCategoryQueryVariables>(getSingleCategoryQuery, {
        variables: { id: categoryId! },
        skip: !categoryId || !!cachedCategory,
        fetchPolicy: 'cache-first',
    });

    if (!categoryId) {
        return { category: null, loading: false, error: undefined };
    }

    const category = cachedCategory ?? networkData?.category ?? null;

    return {
        category,
        loading: loading && !cachedCategory,
        error,
    };
};

export const useCachedCategoriesWithFallback = (): {
    categories: ListCategory[];
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const cachedCategories = useCachedCategories();

    const { data, loading, error } = useQuery<GetCategoriesQuery>(getCategoriesQuery, {
        fetchPolicy: 'cache-first',
    });

    const categories = data?.categories ?? cachedCategories;

    return {
        categories,
        loading: loading && categories.length === 0,
        error,
    };
};
